import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { DraftUpdateSchema, type DraftUpdate } from '@content-platform/shared';
import EditorPlaceholder from '../components/EditorPlaceholder';
import { api, apiErrorMessage } from '../lib/api';

export default function Editor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;

  const queryClient = useQueryClient();

  const draftQuery = useQuery({
    queryKey: ['draft', id],
    queryFn: () => (id ? api.getDraft(id) : null),
    enabled: !!id,
  });

  const { register, handleSubmit, formState } = useForm<DraftUpdate>({
    resolver: zodResolver(DraftUpdateSchema),
    values: draftQuery.data
      ? {
          frontmatter: draftQuery.data.frontmatter,
          body: draftQuery.data.body,
          status: draftQuery.data.status,
        }
      : undefined,
  });

  // frontmatter 전체를 JSON으로 노출 — title/description/slug는 폼 필드가 따로 있고,
  // summary/keyPoints/sources/tags 등 LLM이 채운 보조 필드는 이 JSON으로 편집한다.
  // 리치 폼은 Phase 5.
  const initialJson = useMemo(
    () => (draftQuery.data ? JSON.stringify(draftQuery.data.frontmatter, null, 2) : ''),
    [draftQuery.data],
  );
  const [frontmatterJson, setFrontmatterJson] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [prevInitialJson, setPrevInitialJson] = useState('');
  if (initialJson && prevInitialJson !== initialJson) {
    setPrevInitialJson(initialJson);
    setFrontmatterJson(initialJson);
  }

  const createMutation = useMutation({
    mutationFn: () => api.createDraft(),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['drafts'] });
      navigate(`/editor/${res.draftId}`);
    },
    onError: (err) => {
      toast.error('생성 실패', { description: apiErrorMessage(err) });
    },
  });

  const saveMutation = useMutation({
    mutationFn: (update: DraftUpdate) => {
      if (!id) throw new Error('save: id required');
      return api.updateDraft(id, update);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['drafts'] });
      queryClient.invalidateQueries({ queryKey: ['draft', id] });
    },
    onError: (err) => {
      toast.error('저장 실패', { description: apiErrorMessage(err) });
    },
  });

  const publishMutation = useMutation({
    mutationFn: () => {
      if (!id) throw new Error('publish: id required');
      return api.publish(id);
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['drafts'] });
      toast.success('발행 완료', {
        description: res.url,
        action: {
          label: '열기',
          onClick: () => window.open(res.url, '_blank', 'noopener,noreferrer'),
        },
        duration: 8000,
      });
      navigate('/published');
    },
    onError: (err) => {
      toast.error('발행 실패', { description: apiErrorMessage(err) });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => {
      if (!id) throw new Error('delete: id required');
      return api.deleteDraft(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['drafts'] });
      toast.success('삭제 완료');
      navigate('/');
    },
    onError: (err) => {
      toast.error('삭제 실패', { description: apiErrorMessage(err) });
    },
  });

  if (isNew) {
    return (
      <div>
        <h1 className="text-2xl font-bold">새 컨텐츠</h1>
        <p className="mt-4 text-slate-600">
          대시보드에서 [배치] 실행으로 LLM 가공 draft를 생성하거나, 빈 draft를 만들어
          직접 작성하세요.
        </p>
        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={() => createMutation.mutate()}
            disabled={createMutation.isPending}
            className="rounded bg-slate-900 px-3 py-1.5 text-sm text-white hover:bg-slate-700 disabled:opacity-50"
          >
            {createMutation.isPending ? '생성 중…' : '빈 draft 만들기'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="rounded border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-100"
          >
            대시보드로
          </button>
        </div>
      </div>
    );
  }

  if (draftQuery.isLoading || !draftQuery.data) {
    return <p className="text-slate-500">불러오는 중…</p>;
  }

  // 폼 값 + frontmatter JSON textarea를 merge. JSON parse 실패 시 jsonError 노출 후 null 반환.
  function mergeFrontmatter(data: DraftUpdate): DraftUpdate | null {
    let mergedFrontmatter = data.frontmatter ?? {};
    if (frontmatterJson.trim()) {
      try {
        const parsed = JSON.parse(frontmatterJson);
        // 폼 필드(title/description/slug)가 우선 — JSON에는 다른 보조 필드만 의미 있음
        mergedFrontmatter = { ...parsed, ...(data.frontmatter ?? {}) };
        setJsonError(null);
      } catch (e) {
        setJsonError(`JSON parse 실패: ${(e as Error).message}`);
        return null;
      }
    }
    return { ...data, frontmatter: mergedFrontmatter };
  }

  function onSubmit(data: DraftUpdate) {
    const update = mergeFrontmatter(data);
    if (!update) return;
    saveMutation.mutate(update, { onSuccess: () => toast.success('저장 완료') });
  }

  // 발행은 D1 draft의 body/frontmatter로만 이뤄진다 → 반드시 현재 편집 상태를
  // 먼저 저장(D1 반영)한 뒤 발행한다. 저장 없이 발행하면 편집분이 아닌 원본이 나감.
  async function onPublish(data: DraftUpdate) {
    const update = mergeFrontmatter(data);
    if (!update) return;
    try {
      await saveMutation.mutateAsync(update);
    } catch {
      return; // saveMutation.onError 토스트가 처리
    }
    publishMutation.mutate();
  }

  const busy = saveMutation.isPending || publishMutation.isPending;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <header className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Editor</h1>
        <div className="flex gap-2">
          <button
            type="submit"
            disabled={busy}
            className="rounded bg-slate-900 px-3 py-1.5 text-sm text-white hover:bg-slate-700 disabled:opacity-50"
          >
            {saveMutation.isPending ? '저장 중…' : '저장'}
          </button>
          <button
            type="button"
            onClick={handleSubmit(onPublish)}
            disabled={busy}
            className="rounded bg-emerald-600 px-3 py-1.5 text-sm text-white hover:bg-emerald-500 disabled:opacity-50"
          >
            {publishMutation.isPending ? '발행 중…' : '[발행]'}
          </button>
          <button
            type="button"
            onClick={() => {
              const title = draftQuery.data?.frontmatter.title ?? '(제목 미정)';
              const extra =
                draftQuery.data?.status === 'published'
                  ? '\n\n주의: GitHub repo의 발행 마크다운은 자동 삭제되지 않음.'
                  : '';
              if (confirm(`"${title}" draft를 삭제할까요? 되돌릴 수 없음.${extra}`)) {
                deleteMutation.mutate();
              }
            }}
            disabled={busy || deleteMutation.isPending}
            className="rounded border border-red-300 px-3 py-1.5 text-sm text-red-700 hover:bg-red-50 disabled:opacity-50"
          >
            {deleteMutation.isPending ? '삭제 중…' : '삭제'}
          </button>
        </div>
      </header>

      <Field label="제목">
        <input
          type="text"
          {...register('frontmatter.title')}
          className="block w-full rounded border border-slate-300 bg-white px-3 py-2 focus:border-slate-500 focus:outline-none"
        />
      </Field>

      <Field label="설명 (SEO meta description)">
        <input
          type="text"
          {...register('frontmatter.description')}
          className="block w-full rounded border border-slate-300 bg-white px-3 py-2 focus:border-slate-500 focus:outline-none"
        />
      </Field>

      <Field label="slug (소문자/숫자/하이픈만)">
        <input
          type="text"
          {...register('frontmatter.slug')}
          className="block w-full rounded border border-slate-300 bg-white px-3 py-2 font-mono text-sm focus:border-slate-500 focus:outline-none"
        />
      </Field>

      <Field label="본문 (마크다운)">
        <EditorPlaceholder {...register('body')} />
      </Field>

      <Field label="frontmatter (JSON — summary/keyPoints/sources/tags 등 보조 필드)">
        <textarea
          rows={12}
          value={frontmatterJson}
          onChange={(e) => {
            setFrontmatterJson(e.target.value);
            if (jsonError) setJsonError(null);
          }}
          className="block w-full rounded border border-slate-300 bg-white px-3 py-2 font-mono text-xs focus:border-slate-500 focus:outline-none"
        />
        <p className="mt-1 text-xs text-slate-500">
          위 폼의 제목/설명/slug 값이 우선. JSON에는 summary, keyPoints, sources, tags,
          publishedAt, ogImage, canonicalUrl 등.
        </p>
        {jsonError && <p className="mt-1 text-xs text-red-600">{jsonError}</p>}
      </Field>

      {Object.keys(formState.errors).length > 0 && (
        <pre className="overflow-x-auto rounded bg-red-50 p-3 text-xs text-red-800">
          {JSON.stringify(formState.errors, null, 2)}
        </pre>
      )}
    </form>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-slate-700">{label}</span>
      {children}
    </label>
  );
}
